import { CalendarClock, CloudSun, Gauge, Globe2, GripVertical, HardDrive, Moon, Newspaper, Router } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { DashboardWidgetId } from "../hooks/useDashboardOrder";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDashboardOrder } from "../hooks/useDashboardOrder";
import { useLongPressReorder } from "../hooks/useLongPressReorder";
import type { SpeedTarget } from "../hooks/useSpeedTests";
import { useSpeedTests } from "../hooks/useSpeedTests";
import { buildAstroSummary, formatJstDate, formatJstTime } from "../utils/sky";

type DashboardPanelProps = {
  clockDate: Date;
  skyDate: Date;
};

export function DashboardPanel({ clockDate, skyDate }: DashboardPanelProps) {
  const { weather, news, network, status } = useDashboardData();
  const { order, reorder } = useDashboardOrder();
  const drag = useLongPressReorder<DashboardWidgetId>(reorder);
  const astro = useMemo(() => buildAstroSummary(skyDate), [skyDate]);

  const widgets: Record<DashboardWidgetId, ReactNode> = {
    network: <NetworkCard network={network} state={status.network} />,
    clock: <ClockCard clockDate={clockDate} />,
    astro: <AstroCard astro={astro} />,
    weather: <WeatherCard weather={weather} state={status.weather} />,
    news: <NewsCard news={news} state={status.news} />
  };

  return (
    <aside className="dashboard-panel" aria-label="ダッシュボード">
      {order.map((id) => (
        <section
          key={id}
          className={`dashboard-card dashboard-card-${id} ${drag.activeId === id ? "is-reordering" : ""}`}
          onClickCapture={drag.blockClickIfNeeded}
          {...drag.bind(id)}
        >
          <span className="drag-handle" aria-hidden="true">
            <GripVertical size={14} />
          </span>
          {widgets[id]}
        </section>
      ))}
    </aside>
  );
}

function CardTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <h2>
      {icon}
      {title}
    </h2>
  );
}

function NetworkCard({
  network,
  state
}: {
  network: ReturnType<typeof useDashboardData>["network"];
  state: ReturnType<typeof useDashboardData>["status"]["network"];
}) {
  const { results, run } = useSpeedTests();
  const primary = network?.localSnapshot.primary;
  const tailscale = network?.localSnapshot.secondary.find((item) => item.interface === "utun7");
  const publicIpv6 = primary?.ipv6.find((item) => !item.address.startsWith("fd") && !item.address.startsWith("fe80"));
  const dns = (network?.localSnapshot.dns ?? []).map((item) => (typeof item === "string" ? item : item.address));

  return (
    <>
      <CardTitle icon={<Router size={17} />} title="ネットワーク" />
      {state.error && <p className="card-note">取得失敗: {state.error}</p>}
      <div className="data-list network-data-list" data-testid="network-card">
        <span>
          Primary <strong>{primary ? `${primary.interface} ${primary.ipv4}` : state.loading ? "取得中" : "--"}</strong>
        </span>
        <span>
          Local IPv6 <strong title={publicIpv6?.address}>{shortIp(publicIpv6?.address)}</strong>
        </span>
        <span>
          DNS <strong title={dns.join(" / ")}>{dns.slice(0, 2).map(shortIp).join(" / ") || "--"}</strong>
        </span>
        <span>
          外部IPv4 <strong>{network?.external?.ipv4 ?? (state.loading ? "確認中" : "--")}</strong>
        </span>
        <span>
          外部IPv6 <strong title={network?.external?.ipv6 ?? undefined}>{shortIp(network?.external?.ipv6)}</strong>
        </span>
        <span>
          Tailscale <strong>{tailscale ? `${tailscale.ipv4} / ${shortIp(tailscale.ipv6?.[0]?.address)}` : "--"}</strong>
        </span>
        <span>
          Edge <strong>{network?.edge.colo ?? "Cloudflare"} / {network?.edge.ip ? shortIp(network.edge.ip) : "--"}</strong>
        </span>
      </div>
      {network?.external?.errors.length ? <p className="card-note">外部IP: {network.external.errors.join(" / ")}</p> : null}
      <div className="speed-actions" aria-label="速度測定">
        <SpeedButton target="internet" label="回線" icon={<Gauge size={14} />} state={results.internet.status} onRun={run} />
        <SpeedButton target="fcm" label="fcm.plus" icon={<Globe2 size={14} />} state={results.fcm.status} onRun={run} />
        <SpeedButton target="truenas" label="TrueNAS" icon={<HardDrive size={14} />} state={results.truenas.status} onRun={run} />
      </div>
      <div className="speed-results" aria-live="polite">
        {(Object.keys(results) as SpeedTarget[]).map((target) => (
          <span key={target} className={`speed-result speed-result-${results[target].status}`}>
            {speedLabels[target]} <strong>{results[target].summary}</strong>
            <small>{results[target].detail}</small>
          </span>
        ))}
      </div>
    </>
  );
}

const speedLabels: Record<SpeedTarget, string> = {
  internet: "回線",
  fcm: "fcm.plus",
  truenas: "TrueNAS"
};

function shortIp(value?: string | null) {
  if (!value) return "--";
  if (value.length <= 28) return value;
  return `${value.slice(0, 18)}...${value.slice(-6)}`;
}

function SpeedButton({
  target,
  label,
  icon,
  state,
  onRun
}: {
  target: SpeedTarget;
  label: string;
  icon: ReactNode;
  state: ReturnType<typeof useSpeedTests>["results"][SpeedTarget]["status"];
  onRun: (target: SpeedTarget) => void;
}) {
  return (
    <button
      type="button"
      className="speed-button"
      onClick={() => onRun(target)}
      disabled={state === "running"}
      data-testid={`network-speed-${target}`}
    >
      {icon}
      {state === "running" ? "測定中" : label}
    </button>
  );
}

function ClockCard({ clockDate }: { clockDate: Date }) {
  return (
    <>
      <CardTitle icon={<CalendarClock size={17} />} title="時計 JST+12分" />
      <div className="clock-face">
        <span>{formatJstDate(clockDate)}</span>
        <strong>{formatJstTime(clockDate)}</strong>
      </div>
    </>
  );
}

function AstroCard({ astro }: { astro: ReturnType<typeof buildAstroSummary> }) {
  return (
    <>
      <CardTitle icon={<Moon size={17} />} title="東京の太陽と月" />
      <div className="astro-grid">
        <span>
          Sunrise <strong>{astro.sunrise}</strong>
        </span>
        <span>
          Sunset <strong>{astro.sunset}</strong>
        </span>
        <span>
          Moonrise <strong>{astro.moonrise}</strong>
        </span>
        <span>
          Moonset <strong>{astro.moonset}</strong>
        </span>
      </div>
      <p className="moon-age">
        月齢 {astro.moonAge} / 満月まで {astro.daysToFull} / 新月まで {astro.daysToNew}
      </p>
    </>
  );
}

function WeatherCard({
  weather,
  state
}: {
  weather: ReturnType<typeof useDashboardData>["weather"];
  state: ReturnType<typeof useDashboardData>["status"]["weather"];
}) {
  return (
    <>
      <CardTitle icon={<CloudSun size={17} />} title={`${weather?.location ?? "渋谷"} 週間天気`} />
      <div className="weather-now">
        <strong>{weather?.current.temperature ?? "--"}°</strong>
        <span>{weather?.current.label ?? (state.loading ? "取得中" : "取得失敗")}</span>
      </div>
      {state.error && <p className="card-note">取得失敗: {state.error}</p>}
      <div className="forecast-row weekly-forecast">
        {(weather?.daily ?? []).slice(0, 7).map((day) => (
          <span key={day.date}>
            <em>{formatForecastDate(day.date)}</em>
            <small>{day.label}</small>
            <strong>
              {day.max ?? "--"}/{day.min ?? "--"}°
            </strong>
            <b>{day.rain ?? "--"}%</b>
          </span>
        ))}
      </div>
    </>
  );
}

function formatForecastDate(value: string) {
  const date = new Date(`${value}T00:00:00+09:00`);
  const weekday = new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", weekday: "short" }).format(date);
  return `${value.slice(5).replace("-", "/")} ${weekday}`;
}

function NewsCard({
  news,
  state
}: {
  news: ReturnType<typeof useDashboardData>["news"];
  state: ReturnType<typeof useDashboardData>["status"]["news"];
}) {
  return (
    <>
      <CardTitle icon={<Newspaper size={17} />} title="Google News" />
      <div className="headline-list">
        {(news?.items ?? []).slice(0, 4).map((item) => (
          <a key={item.link || item.title} href={item.link} target="_blank" rel="noreferrer">
            {item.title}
          </a>
        ))}
        {!news && <span>{state.loading ? "見出しを取得中..." : `取得失敗: ${state.error || "--"}`}</span>}
      </div>
    </>
  );
}
